import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/** Absolute URL for a backend path — for links the browser navigates to
 *  directly (calendar downloads), which must bypass the axios instance. */
export const apiUrl = (path: string): string => `${BASE_URL}${path}`;

const api = axios.create({
  baseURL: BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('aura_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Surface server-side correlation ids for unexpected (5xx) failures in the
// browser console so a user report can be matched to backend/Sentry logs.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status && status >= 500) {
      const errorId = error?.response?.data?.error_id;
      const method = error?.config?.method?.toUpperCase() ?? 'REQUEST';
      const url = error?.config?.url ?? '(unknown url)';
      console.error(`API error ${status} on ${method} ${url}` + (errorId ? ` [error_id=${errorId}]` : ''));
    }
    return Promise.reject(error);
  }
);

export interface InterviewPlan {
  candidate_name: string;
  extracted_skills: string[];
  question_bank: string[];
  /** Voice id chosen for the interviewer (see getVoices); absent = default. */
  voice?: string | null;
}

export interface VoiceOption {
  id: string;
  label: string;
  description: string;
  gender?: 'male' | 'female' | null;
}

export interface UploadResponse {
  session_id: string;
  plan_summary: InterviewPlan;
}

export interface SectionGrade {
  section_name: string;
  score: number;
  comments: string;
}

export interface FinalReport {
  candidate_name: string;
  overall_score: number;
  section_grades: SectionGrade[];
  strengths: string[];
  weaknesses: string[];
  recommendation: 'Hire' | 'No Hire' | 'Strong Hire' | 'Hold';
  summary: string;
}

export const uploadResume = async (file: File, jobDescription?: string): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (jobDescription && jobDescription.trim()) {
    formData.append('job_description', jobDescription.trim());
  }
  const response = await api.post<UploadResponse>('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getVoices = async (): Promise<VoiceOption[]> => {
  const response = await api.get<{ voices: VoiceOption[] }>('/voices');
  return response.data.voices;
};

export const getToken = async (sessionId: string, voice?: string): Promise<string> => {
  const params = new URLSearchParams({ session_id: sessionId });
  // The choice always travels with the token request (default included):
  // /token persists it into the session plan, so re-picking the default on a
  // rejoined session clears a previously stored voice instead of silently
  // keeping it.
  if (voice) {
    params.set('voice', voice);
  }
  const response = await api.get<{ token: string }>(`/token?${params.toString()}`);
  return response.data.token;
};

export const getPlan = async (sessionId: string): Promise<{ plan: InterviewPlan; user_id: string; user_email: string }> => {
  const response = await api.get<{ plan: InterviewPlan; user_id: string; user_email: string }>(`/plan/${sessionId}`);
  return response.data;
};

export const getReport = async (sessionId: string): Promise<FinalReport> => {
  const response = await api.get<FinalReport>(`/report/${sessionId}`);
  return response.data;
};

export const downloadArtifact = async (
  sessionId: string,
  fileType: 'pdf' | 'transcript',
  candidateName?: string,
): Promise<void> => {
  const response = await api.get(`/download/${sessionId}/${fileType}`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  // Recruiters download several reports at once — prefix with the candidate
  // name so the files don't collide into report.pdf / transcript.json.
  const ext = fileType === 'pdf' ? 'pdf' : 'json';
  const label = fileType === 'pdf' ? 'report' : 'transcript';
  const slug = candidateName?.replace(/\s+/g, '-').toLowerCase();
  link.download = slug ? `aura-${label}-${slug}.${ext}` : `${label}.${ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const uploadPdf = async (sessionId: string, pdfBlob: Blob): Promise<void> => {
  const formData = new FormData();
  formData.append('file', pdfBlob, 'report.pdf');
  await api.post(`/upload-pdf/${sessionId}`, formData);
};

export interface SessionSummary {
  session_id: string;
  candidate_name: string;
  overall_score: number | null;
  recommendation: 'Hire' | 'No Hire' | 'Strong Hire' | 'Hold' | null;
  status: 'pending' | 'in_progress' | 'completed';
  created_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
}

export interface TranscriptEntryRead {
  speaker: string;
  text: string;
  timestamp_s: number;
}

// Integrity signals (proctoring v1 + trust signals v2): browser-collected, no
// camera/biometrics. The v1 types (tab/blur/paste) come from the browser; the
// v2 types are worker/evaluator-detected hints (long dead air before a fluent
// answer, repeated takes, a speaking-rate jump, "reads as scripted" answers).
// The backend stores them for human review and never feeds them to the
// hiring-report LLM, so they render as their own block, not a score.
export type IntegrityEventType =
  | 'tab_hidden'
  | 'tab_visible'
  | 'window_blur'
  | 'window_focus'
  | 'paste'
  | 'long_pause_then_fluent'
  | 'answer_repeat'
  | 'speaking_rate_shift'
  | 'scripted_answer';

export interface IntegrityEvent {
  type: IntegrityEventType;
  /** Milliseconds since the candidate joined the room (client monotonic clock). */
  at_ms: number;
}

export interface SessionDetail {
  session_id: string;
  candidate_name: string;
  user_email: string;
  user_id: string;
  plan: InterviewPlan | null;
  report: FinalReport | null;
  transcript: TranscriptEntryRead[] | null;
  integrity_events: IntegrityEvent[] | null;
  /** Whether the session's invite opted into proctoring; null = no invite (practice). */
  proctored: boolean | null;
  status: 'pending' | 'in_progress' | 'completed';
  created_at: string;
  completed_at: string | null;
}

export const listAdminSessions = async (
  params: { status?: 'pending' | 'in_progress' | 'completed'; limit?: number; offset?: number } = {}
): Promise<SessionSummary[]> => {
  const response = await api.get<SessionSummary[]>('/admin/sessions', { params });
  return response.data;
};

export const listMySessions = async (): Promise<SessionSummary[]> => {
  const response = await api.get<SessionSummary[]>('/sessions/mine');
  return response.data;
};

export const getAdminSessionDetail = async (sessionId: string): Promise<SessionDetail> => {
  const response = await api.get<SessionDetail>(`/admin/sessions/${sessionId}/detail`);
  return response.data;
};

export const getMySessionDetail = async (sessionId: string): Promise<SessionDetail> => {
  const response = await api.get<SessionDetail>(`/sessions/${sessionId}/detail`);
  return response.data;
};

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'candidate' | 'recruiter' | '';
  avatar_url?: string | null;
  /** Method that created the account ("email" | "google" | "github"). */
  auth_provider?: 'email' | 'google' | 'github';
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

/** Pull a human-readable message out of an axios error from our API. */
export const apiErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.'): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    // 422 handler: { detail: { message, errors: [{ field, message }] } }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      const fieldErrors = Array.isArray(detail.errors)
        ? detail.errors
            // pydantic v2 prefixes validator ValueErrors with "Value error, " — drop it
            .map((e: { field?: string; message?: string }) => {
              const message = (e?.message ?? '').replace(/^Value error,\s*/, '')
              return e?.field ? `${e.field}: ${message}` : message
            })
            .filter(Boolean)
            .slice(0, 3)
        : [];
      return fieldErrors.length > 0 ? `${detail.message} (${fieldErrors.join('; ')})` : detail.message;
    }
    // slowapi 429 bodies use { error: "..." } instead of detail
    if (typeof data?.error === 'string') return data.error;
  }
  return fallback;
};

export const registerUser = async (email: string, password: string, name: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/auth/register', { email, password, name });
  return response.data;
};

export const loginWithPassword = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>('/auth/login', { email, password });
  return response.data;
};

export const resendVerification = async (email: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/auth/resend-verification', { email });
  return response.data;
};

export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data;
};

export default api;


// ---------------------------------------------------------------- Invites

export interface InviteOut {
  invite_id: string;
  title: string;
  context: string | null;
  questions: string[];
  token: string;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  // When the candidate claimed the link — the funnel's "started" stage;
  // null while the invite is still awaiting a candidate.
  redeemed_at: string | null;
  completed_at: string | null;
  // When the link stops being redeemable; null = no expiry (legacy invites).
  expires_at: string | null;
  // Opt-in integrity signals for this invite's interview (see IntegrityEvent).
  proctoring: boolean;
  candidate_user_id: string | null;
  // Delivery address when the recruiter opted into emailing the candidate.
  candidate_email: string | null;
  session_id: string | null;
  candidate_name: string | null;
  overall_score: number | null;
  recommendation: 'Hire' | 'No Hire' | 'Strong Hire' | 'Hold' | null;
  // {section_name: score} joined from the linked session's report — the
  // compare view ranks by section off this list response alone. Null = no report.
  section_scores: Record<string, number> | null;
  // Flagged integrity events (tab switches, focus loss, paste — recovery
  // events excluded). Null = no signals recorded (not proctored / no
  // session); 0 is a clean proctored run.
  integrity_flags: number | null;
}

export interface RecruiterInvitesResponse {
  invites: InviteOut[];
  quota_used: number;
  quota_limit: number;
}

export interface InviteDetail extends InviteOut {
  report: FinalReport | null;
  transcript: TranscriptEntryRead[] | null;
  integrity_events: IntegrityEvent[] | null;
  // Read-only report sharing state of the linked session.
  share_enabled: boolean;
  share_token: string | null;
  // The invite owner's booking link, when set — "Book a follow-up
  // interview" on a completed report.
  booking_url: string | null;
}

export interface InvitePreview {
  title: string;
  context: string | null;
  questions: string[];
  recruiter_name: string;
  expires_at: string | null;
  proctoring: boolean;
  recruiter_company?: string | null;
}

export interface InviteStartResponse {
  session_id: string;
  plan: InterviewPlan;
}

export interface InviteCreatePayload {
  title: string;
  context?: string;
  questions: string[];
  /** Hours the link stays redeemable (1–720). Omitted = the API's 24h default. */
  expires_in_hours?: number;
  /** Record tab/focus/paste signals during the interview, shown to the recruiter. */
  proctoring?: boolean;
  /** Optional delivery address — when set, Aura emails the candidate the link and deadline. */
  candidate_email?: string;
  /** Optional custom link name used as the invite token, so the share URL reads
   *  /invite/{slug}. Omitted or empty mints a random, unguessable link. */
  slug?: string;
}

export const createInvite = async (payload: InviteCreatePayload): Promise<InviteOut> => {
  const response = await api.post<InviteOut>('/recruiter/invites', payload);
  return response.data;
};

export const resendInviteEmail = async (inviteId: string): Promise<InviteOut> => {
  const response = await api.post<InviteOut>(`/recruiter/invites/${inviteId}/resend-email`);
  return response.data;
};

/** List the recruiter's invites. Pass `limit` to widen past the endpoint's
 *  50-row default (200 is the server ceiling) — the dashboard funnel derives
 *  from this page, so the default would truncate it for active accounts. */
export const listRecruiterInvites = async (limit?: number): Promise<RecruiterInvitesResponse> => {
  const response = await api.get<RecruiterInvitesResponse>('/recruiter/invites', {
    params: limit !== undefined ? { limit } : undefined,
  });
  return response.data;
};

export const getRecruiterInvite = async (inviteId: string): Promise<InviteDetail> => {
  const response = await api.get<InviteDetail>(`/recruiter/invites/${inviteId}`);
  return response.data;
};

export const cancelInvite = async (inviteId: string): Promise<InviteOut> => {
  const response = await api.post<InviteOut>(`/recruiter/invites/${inviteId}/cancel`);
  return response.data;
};

/** Download the recruiter's candidates-and-scores export (RFC 4180 CSV).
 *  The month in the filename is UTC, matching the server's own naming. */
export const exportRecruiterInvitesCsv = async (): Promise<void> => {
  const month = new Date().toISOString().slice(0, 7); // YYYY-MM
  await downloadFile('/recruiter/invites/export', `aura-candidates-${month}.csv`);
};

export const getInvitePreview = async (token: string): Promise<InvitePreview> => {
  const response = await api.get<InvitePreview>(`/invite/${token}`);
  return response.data;
};

export const startInvite = async (token: string): Promise<InviteStartResponse> => {
  const response = await api.post<InviteStartResponse>(`/invite/${token}/start`);
  return response.data;
};

export const uploadAudio = async (sessionId: string, blob: Blob, ext: 'webm' | 'mp4'): Promise<void> => {
  const formData = new FormData();
  formData.append('file', blob, `audio.${ext}`);
  await api.post(`/audio/${sessionId}`, formData);
};

// ---------------------------------------------------------------- Sharing

// Read-only report sharing: the token IS the auth for the public page, so it
// is only ever returned over endpoints gated by the session's access rule.
export interface ShareInfo {
  share_token: string | null;
  share_enabled: boolean;
}

/** Enable (or re-enable) sharing; returns the live token (idempotent). */
export const enableSessionShare = async (sessionId: string): Promise<ShareInfo> => {
  const response = await api.post<ShareInfo>(`/sessions/${sessionId}/share`);
  return response.data;
};

/** Revoke the share link — the public URL stops resolving immediately. */
export const revokeSessionShare = async (sessionId: string): Promise<ShareInfo> => {
  const response = await api.delete<ShareInfo>(`/sessions/${sessionId}/share`);
  return response.data;
};

export interface SharedReport {
  report: FinalReport;
  transcript: TranscriptEntryRead[] | null;
  /** The invite's title (role interviewed for); null for practice sessions. */
  role_title: string | null;
  /** Display name for the "Shared by" header; empty when there is no invite. */
  shared_by: string;
  completed_at: string | null;
  /** Flagged integrity-signal counts; {} = proctored, no flags; null = not proctored. */
  integrity_counts: Record<string, number> | null;
}

/** Public endpoint: fetch a shared report by token, no account needed. */
export const getSharedReport = async (token: string): Promise<SharedReport> => {
  const response = await api.get<SharedReport>(`/share/report/${token}`);
  return response.data;
};

/** Flush a batch of integrity events for a proctored session. */
export const postIntegrityEvents = async (sessionId: string, events: IntegrityEvent[]): Promise<void> => {
  await api.post(`/integrity/${sessionId}`, { events });
};

export const setUserRole = async (role: 'candidate' | 'recruiter'): Promise<LoginResponse> => {
  const response = await api.post('/auth/role', { role });
  return response.data;
};

// ---------------------------------------------------------------- Profiles

export interface ExperienceEntry {
  title: string;
  company: string;
  start: string;
  end: string;
  description: string;
}

export interface EducationEntry {
  school: string;
  degree: string;
  field: string;
  start: string;
  end: string;
}

export interface CandidateProfile {
  headline: string;
  location: string;
  summary: string;
  skills: string[];
  experience: ExperienceEntry[];
  education: EducationEntry[];
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  resume_stored: boolean;
  resume_uploaded_at: string | null;
}

export interface ParsedResumeProfile {
  headline: string | null;
  location: string | null;
  summary: string | null;
  skills: string[];
  experience: ExperienceEntry[];
  education: EducationEntry[];
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
}

export interface ResumeParseResponse {
  parsed: ParsedResumeProfile;
  resume_stored: boolean;
}

export interface RecruiterProfile {
  company_name: string;
  job_title: string;
  company_website: string | null;
  company_location: string;
  /** Email me when a candidate completes my interview (default on). */
  notify_completed: boolean;
  /** Email me the moment a candidate redeems a link (default off — noisy). */
  notify_started: boolean;
  /** Email me when a pending link is ~24h from expiring (default on). */
  notify_expiring: boolean;
  // Scheduler passthrough (Calendly/Cal.com/any https URL) shown on completed reports.
  booking_url: string | null;
  // Outbound completion webhook target (Zapier/Make/n8n). The secret is never
  // returned after save — webhook_configured says whether one is on file.
  webhook_url: string | null;
  webhook_configured: boolean;
  /** True when the endpoint was auto-disabled after repeated delivery failures. */
  webhook_disabled: boolean;
}

/** Save payload for PUT /profile/recruiter. */
export interface RecruiterProfileInput {
  company_name: string;
  job_title: string;
  company_website: string | null;
  company_location: string;
  notify_completed: boolean;
  notify_started: boolean;
  notify_expiring: boolean;
  booking_url: string | null;
  webhook_url: string | null;
  /** Write-only: omit or send null to keep the stored secret unchanged. */
  webhook_secret?: string | null;
}

export const getCandidateProfile = async (): Promise<CandidateProfile> => {
  const response = await api.get<CandidateProfile>('/profile/candidate');
  return response.data;
};

export const saveCandidateProfile = async (payload: Omit<CandidateProfile, 'resume_stored' | 'resume_uploaded_at'>): Promise<CandidateProfile> => {
  const response = await api.put<CandidateProfile>('/profile/candidate', payload);
  return response.data;
};

/** Upload a resume PDF; returns parsed fields for review (nothing is auto-saved). */
export const uploadProfileResume = async (file: File): Promise<ResumeParseResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<ResumeParseResponse>('/profile/candidate/resume', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getRecruiterProfile = async (): Promise<RecruiterProfile> => {
  const response = await api.get<RecruiterProfile>('/profile/recruiter');
  return response.data;
};

export const saveRecruiterProfile = async (payload: RecruiterProfileInput): Promise<RecruiterProfile> => {
  const response = await api.put<RecruiterProfile>('/profile/recruiter', payload);
  return response.data;
};

/** Send a sample completion event to the recruiter's webhook (no retries).
 * Probes the given form values when supplied; null/omitted fields fall back
 * to the stored config, so an unsaved endpoint is what actually gets tested. */
export const sendRecruiterWebhookTest = async (
  webhookUrl?: string | null,
  webhookSecret?: string | null,
): Promise<{ ok: boolean; detail: string }> => {
  const response = await api.post<{ ok: boolean; detail: string }>('/profile/recruiter/webhook/test', {
    webhook_url: webhookUrl ?? null,
    webhook_secret: webhookSecret ?? null,
  });
  return response.data;
};

/** Start a practice interview from the resume already on the profile. */
export const startInterviewFromProfile = async (): Promise<UploadResponse> => {
  const response = await api.post<UploadResponse>('/profile/candidate/interview');
  return response.data;
};

/** Fetch a protected artifact as a blob (plain links cannot carry the JWT header). */
export const fetchBlob = async (path: string): Promise<Blob> => {
  const response = await api.get(path, { responseType: 'blob' });
  return response.data;
};

/**
 * Save an in-memory blob via anchor click. Used when the artifact is already
 * loaded in memory (inline players hold it for playback) — re-fetching to
 * download the same bytes doubles the artifact-endpoint hit for zero benefit.
 */
export const saveBlob = async (blob: Blob, filename: string): Promise<void> => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** Download a protected artifact via authenticated fetch + anchor click. */
export const downloadFile = async (path: string, filename: string): Promise<void> => {
  const blob = await fetchBlob(path);
  await saveBlob(blob, filename);
};

/**
 * The recording endpoint stores webm (Chrome/Firefox) or mp4 (Safari) and
 * states which in the response MIME type. Derive the saved file's extension
 * from the blob rather than re-declaring the endpoint's choice.
 */
export const recordingExtension = (blob: Blob): string =>
  blob.type.includes('mp4') ? 'mp4' : 'webm';
